import axios, { AxiosInstance } from "axios";

interface User {
  id: string;
  names?: string;
  name?: string;
  fullName?: string;
  email?: string;
  status?: string;
  role?: { name: string } | string | null;
  leadershipRole?: string | null;
  isAdmin?: boolean;
  isGroupLeader?: boolean;
  type?: string;
  requiresPasswordChange?: boolean;
  [key: string]: any; // Allow additional properties
}

class AuthService {
  private api: AxiosInstance;
  private tokenKey: string = "access_token";
  private user: User | null = null;

  constructor() {
    // Get API URL from runtime config (for Dokploy auto-generated domains) or fallback
    const getApiUrl = (): string => {
      // Priority 1: Runtime config (for Dokploy auto-generated domains)
      if (typeof window !== 'undefined' && (window as any).__APP_CONFIG__?.API_URL !== undefined) {
        const runtimeUrl = (window as any).__APP_CONFIG__.API_URL;
        // Allow empty string for relative URLs, but skip if it's a placeholder
        if (runtimeUrl !== null && !runtimeUrl.includes('{{')) {
          return runtimeUrl; // Can be empty string for relative URLs
        }
      }
      
      // Priority 2: Build-time environment variable (Vite)
      if (import.meta.env.VITE_PUBLIC_API_URL) {
        return import.meta.env.VITE_PUBLIC_API_URL;
      }
      
      // Priority 3: Default fallback (use relative URL for all-in-one deployments)
      return import.meta.env.PROD 
        ? '' // Empty string = relative URL (same domain) - works for all-in-one deployments
        : 'http://localhost:5000'; // Local development backend on port 5000
    };
    
    const apiUrl = getApiUrl();
    this.api = axios.create({
      baseURL: apiUrl + "/api",
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  async refresh_token() {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      console.error("No refresh token found in localStorage");
      throw new Error("No refreshToken, please login again");
    }

    try {
      const response = await this.api.post("/auth/refresh-token", {
        refresh_token: refreshToken,
      });
      
      if (response.data?.access_token) {
        this.setToken(response.data.access_token);
        // Update user if provided
        if (response.data?.user) {
          this.user = response.data.user;
          localStorage.setItem('user', JSON.stringify(this.user));
        }
        return { access_token: response.data.access_token, user: response.data.user };
      } else {
        throw new Error("No access token in refresh response");
      }
    } catch (e: any) {
      console.error("Refresh token error:", {
        status: e.response?.status,
        message: e.message,
        data: e.response?.data
      });
      
      // If refresh fails, logout user
      if (e.response?.status === 401 || e.response?.status === 404) {
        console.error("Refresh token invalid or endpoint not found, logging out");
        await this.logout();
      }
      throw e; // Re-throw so interceptor can handle it
    }
  }

  public getToken(): string | null {
    const token = localStorage.getItem(this.tokenKey);
    if (!token || token === "null" || token === "undefined") {
      return null;
    }
    return token;
  }

  async updateProfile(data: { name: string }): Promise<User | null> {
    const token = authService.getToken();
    const response = await this.api.put("/auth/update-profile", data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    this.user = response.data;
    return this.user;
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const token = authService.getToken();
      if (!token) {
        this.logout();
        return null;
      }
      const response = await this.api.get("/auth/current", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // Backend returns data directly or wrapped in 'user' property
      this.user = response.data.user || response.data;
      
      console.log("[AuthService] getCurrentUser - Full response.data:", JSON.stringify(response.data, null, 2));
      console.log("[AuthService] getCurrentUser - response.data.user:", response.data.user);
      console.log("[AuthService] getCurrentUser - response.data (direct):", response.data);
      console.log("[AuthService] getCurrentUser - Final user object:", JSON.stringify(this.user, null, 2));
      
      const userAny = this.user as any;
      console.log("[AuthService] Leadership role from user object:", userAny?.leadershipRole);
      console.log("[AuthService] Role object:", userAny?.role);
      console.log("[AuthService] Role name:", userAny?.role?.name);
      console.log("[AuthService] IsAdmin:", userAny?.isAdmin);
      
      // Preserve requiresPasswordChange flag from response
      if (this.user && response.data?.user) {
        this.user = { ...this.user, requiresPasswordChange: response.data.user.requiresPasswordChange } as User;
      }
      
      // Ensure leadershipRole is preserved - check all possible sources
      if (this.user) {
        const userAny = this.user as any;
        // Get leadershipRole from multiple possible sources
        const leadershipRole = userAny.leadershipRole || 
                              (userAny.role && typeof userAny.role === 'object' ? userAny.role.name : null) ||
                              (typeof userAny.role === 'string' && ["Technical Support", "President", "Secretary", "Accountant"].includes(userAny.role) ? userAny.role : null);
        
        if (leadershipRole) {
          (this.user as any).leadershipRole = leadershipRole;
          console.log("[AuthService] Set leadershipRole to:", leadershipRole);
        } else {
          console.log("[AuthService] WARNING: No leadershipRole found in user object!");
        }
      }
      
      // Save user to localStorage
      if (this.user) {
        localStorage.setItem('user', JSON.stringify(this.user));
        const userAny = this.user as any;
        console.log("[AuthService] Saved user to localStorage with leadershipRole:", userAny.leadershipRole);
      }
      
      return this.user;
    } catch (error: any) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;
      const message = error?.response?.data?.message || "";

      // Token expired or invalid: clean up and signal caller
      if (status === 401 && (code === "TOKEN_EXPIRED" || message.toLowerCase().includes("expired"))) {
        console.warn("[AuthService] Token expired, logging out");
        this.logout();
        // Throw so callers (auth.context) know to redirect
        throw Object.assign(new Error("Session expired. Please login again."), { code: "TOKEN_EXPIRED" });
      }

      console.error("[AuthService] Error getting current user:", error);
      return null;
    }
  }

  async googleLogin({
    token,
    platform,
  }: {
    token: string;
    platform: string;
  }): Promise<User | null> {
    const response = await this.api.post("/auth/google-login", {
      token,
      platform,
    });

    this.setToken(response.data?.access_token);
    this.user = response.data;
    return this.user;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  private setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  logout(): void {
    this.removeToken();
    this.user = null;
    localStorage.removeItem('user');
  }

  async signIn(credentials: { email: string; password: string }) {
    console.log("[AuthService] signIn called with:", credentials.email);
    const response = await this.api.post("/auth/login", credentials);
    console.log("[AuthService] Login response data:", response.data);
    
    // Support both { access_token, user } and { data: { token, user } } shapes
    const data = response.data;
    const accessToken = data?.access_token || data?.data?.token || data?.data?.access_token;
    const refreshToken = data?.refresh_token || data?.data?.refresh_token;
    const userData = data?.user || data?.data?.user;
    
    this.setToken(accessToken);
    
    // Save refresh token if provided
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
      console.log("[AuthService] Refresh token saved");
    }
    
    this.user = userData;
    
    console.log("[AuthService] User from response:", this.user);
    const userAny = this.user as any;
    console.log("[AuthService] Leadership role from response:", userAny?.leadershipRole || userAny?.role?.name);
    console.log("[AuthService] IsAdmin from response:", userAny?.isAdmin);
    console.log("[AuthService] requiresPasswordChange in response.user:", userData?.requiresPasswordChange);
    
    // Preserve requiresPasswordChange flag from response
    if (this.user && userData) {
      const userAny = this.user as any;
      this.user = { 
        ...this.user, 
        requiresPasswordChange: userData.requiresPasswordChange,
        // Ensure leadershipRole is preserved
        leadershipRole: userAny.leadershipRole || userAny.role?.name || null
      } as any;
      console.log("[AuthService] User after preserving flags:", this.user);
      const updatedUserAny = this.user as any;
      console.log("[AuthService] Leadership role after preservation:", updatedUserAny.leadershipRole);
    }
    
    // Save user to localStorage
    if (this.user) {
      localStorage.setItem('user', JSON.stringify(this.user));
      const userAny = this.user as any;
      console.log("[AuthService] Saved user to localStorage with leadershipRole:", userAny.leadershipRole);
    }
    
    console.log("[AuthService] Returning user:", this.user);
    return this.user;
  }

  async signUp(credentials: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
  }) {
    const response = await this.api.post("/auth/register", credentials);
    this.setToken(response.data?.access_token);
    this.user = response.data;
    return this.user;
  }

  async forgotPassword({ email }) {
    const response = await this.api.post("/auth/forgot-password", { email });
    return response;
  }

  async resetPassword({ password, token }) {
    const response = await this.api.post("/auth/reset-password", {
      password,
      token,
    });
    return response;
  }

  private removeToken(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem("refreshToken");
  }
}

const authService = new AuthService();

export default authService;
